import type { CSSProperties } from "react";
import { ArrowUpRight, Lock, MessageSquareText, Users } from "lucide-react";
import type { Space } from "../../api/spaces";

type Props = {
    space: Space;
    onOpen: (spaceId: string) => void;
    onJoin?: (spaceId: string) => void;
    joining?: boolean;
    compact?: boolean;
    authorityView?: boolean;
    openOnly?: boolean;
};

function cleanAccent(value?: string) {
    return /^#[0-9a-fA-F]{6}$/.test(value || "") ? String(value) : "#22d3ee";
}

function stableSeed(value: string) {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) % 9973;
    }
    return hash;
}

function activityLabel(space: Space) {
    if (space.isArchived) return "Preserved history";
    if (space.privateStats) return "Private community";
    const online = Number(String(space.online || "0").replace(/[^0-9.]/g, ""));
    if (online > 0) return `${space.online} active now`;
    const posts = space.activity?.posts || 0;
    return posts === 1 ? "1 post" : `${posts} posts`;
}

export default function SpaceCommunityCard({ space, onOpen, onJoin, joining = false, compact = false, authorityView = false, openOnly = false }: Props) {
    const accent = cleanAccent(space.accentColor);
    const seed = stableSeed(`${space.id}:${space.title}`);
    const silhouetteStyle = {
        "--space-accent": accent,
        "--space-glow-x": `${22 + (seed % 52)}%`,
        "--space-glow-y": `${16 + (seed % 36)}%`,
        "--space-orbit-x": `${48 + (seed % 28)}%`,
        "--space-orbit-y": `${12 + (seed % 42)}%`,
        "--space-orbit-size": `${6.2 + (seed % 18) / 10}rem`,
    } as CSSProperties;
    const isMember = Boolean(authorityView || space.canManage || space.isJoined || space.id === "blackspace");
    const canRequestJoin = Boolean(onJoin && !openOnly);
    const accessMode = space.accessMode || "open";
    const action = space.isArchived
        ? "Open"
        : isMember
        ? "Open"
        : canRequestJoin && accessMode === "open"
            ? "Join"
        : accessMode === "private"
            ? "Invite required"
            : accessMode === "locked" || accessMode === "sealed" || space.requiresInvite
                ? "Unlock"
                : "Open";
    const actionTone = space.isArchived
        ? "text-amber-200 hover:text-amber-50"
        : action === "Join"
            ? "text-cyan-200 hover:text-white"
            : action === "Unlock"
                ? "text-violet-200 hover:text-white"
                : "text-neutral-200 hover:text-cyan-100";

    function handleAction() {
        if (action === "Join" && canRequestJoin) onJoin?.(space.id);
        else onOpen(space.id);
    }

    return (
        <article
            className={`bs-community-card bs-space-card group relative overflow-hidden rounded-2xl border border-white/10 bg-neutral-950 transition hover:-translate-y-0.5 hover:border-white/20 ${compact ? "w-[17rem] shrink-0" : "h-full"}`}
            style={silhouetteStyle}
        >
            <button
                type="button"
                onClick={() => onOpen(space.id)}
                className="bs-space-card-hit absolute inset-0 z-10 rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300"
                aria-label={`Open ${space.title}`}
            />

            <div className="bs-space-card-ambient" aria-hidden="true" />
            {!space.isArchived && accessMode === "open" && <span className="bs-space-card-live-dot" aria-hidden="true" />}

            <div className="bs-space-card-body pointer-events-none relative z-20 p-4">
                <div className="flex items-start gap-3">
                    <div className="bs-space-card-mark grid place-items-center text-lg font-black text-white">
                        {(space.title || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                            <h3 className="truncate text-base font-black text-white group-hover:text-cyan-100">{space.title}</h3>
                            {space.isArchived && <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-amber-200">Archived</span>}
                            {!space.isArchived && space.accessMode && space.accessMode !== "open" && <Lock size={12} className="shrink-0 text-neutral-500" aria-label="Restricted access" />}
                        </div>
                        <p className="mt-1 line-clamp-2 text-sm leading-5 text-neutral-400">
                            {space.desc || "A community on Blackspace."}
                        </p>
                    </div>
                </div>

                <div className="bs-space-card-footer mt-4 flex items-center justify-between gap-3">
                    <div className="bs-space-card-stats flex min-w-0 items-center gap-3 text-xs font-semibold text-neutral-500">
                        <span className="inline-flex items-center gap-1.5">
                            <Users size={13} />
                            {space.privateStats ? "Members" : space.isArchived ? `${space.archivedMemberCount || 0} former members` : `${space.members || "0"} members`}
                        </span>
                        <span className="inline-flex min-w-0 items-center gap-1.5 truncate">
                            <MessageSquareText size={13} />
                            {activityLabel(space)}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleAction();
                        }}
                        disabled={joining}
                        className={`bs-space-card-action pointer-events-auto relative z-30 inline-flex shrink-0 items-center gap-1 py-1 text-[11px] font-black leading-none tracking-wide transition disabled:opacity-60 ${actionTone}`}
                        aria-label={`${action} ${space.title}`}
                    >
                        {joining ? "Joining" : action}
                        {!joining && <ArrowUpRight size={13} />}
                    </button>
                </div>
            </div>
        </article>
    );
}
